<template lang="pug">
v-dialog(
  v-model="dialogOpen",
  :max-width="maxWidth",
  transition="dialog-transition"
)
  v-card.companies-dialog(v-if="dialogOpen")
    .companies-dialog__header
      .companies-dialog__heading
        .companies-dialog__title {{ title }}
        .companies-dialog__subtitle(v-if="itemLabel") {{ itemLabel }}

    v-divider

    .companies-dialog__body
      v-data-table.companies-dialog__table(
        :headers="headers",
        :items="details",
        :loading="loading",
        :items-per-page="10",
        :footer-props="footerProps",
        locale="pt-BR",
        no-data-text="Nenhum registro encontrado"
      )

    v-divider

    v-card-actions.companies-dialog__actions
      v-spacer
      v-btn.mr-1(
        color="primary",
        text,
        @click="exportarCSV",
        :disabled="!details.length || loading"
      )
        v-icon(left, small) mdi-file-table-outline
        span Exportar
      v-btn(color="primary", text, @click="dialogOpen = false")
        v-icon(left, small) mdi-close
        span Fechar
</template>

<script>
import { dashboard as dashboardApi } from "@/api";

const DETAIL_HEADERS = [
  { text: "Código", value: "codigo", width: "60px", sortable: false },
  { text: "Cliente", value: "empresa", width: "240px" },
  { text: "Cód.", value: "codigoempresa", width: "60px", sortable: false },
  { text: "Empresa", value: "fantasia", width: "240px" },
  { text: "UF", value: "uf", width: "50px", sortable: false },
  { text: "HASH", value: "base", width: "120px", sortable: false },
  { text: "Softhouse", value: "sistema", width: "120px" },
];

const DETAIL_FIELDS = DETAIL_HEADERS.map((header) => header.value);

export default {
  name: "CompaniesDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    itemLabel: {
      type: String,
      default: "",
    },
    itemKey: {
      type: String,
      default: null,
    },
    processo: {
      type: String,
      default: null,
    },
    maxWidth: {
      type: [Number, String],
      default: 960,
    },
  },
  data: () => ({
    headers: DETAIL_HEADERS,
    details: [],
    loading: false,
    footerProps: {
      itemsPerPageOptions: [10, 25, 50, 100, -1],
      showCurrentPage: true,
      showFirstLastPage: true,
      itemsPerPageText: "Itens por página",
      itemsPerPageAllText: "Todos",
    },
  }),
  computed: {
    dialogOpen: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  watch: {
    dialogOpen: {
      immediate: true,
      handler(open) {
        if (open) {
          this.fetchDetails();
        }
      },
    },
  },
  methods: {
    async fetchDetails() {
      this.loading = true;
      this.details = [];
      this.headers = [...DETAIL_HEADERS];

      try {
        const response = await dashboardApi.getEmpresasDetalhes(
          this.processo,
          this.itemKey
        );

        this.details = this.parseRegistros(response?.registros);
      } catch (error) {
        this.$toast.error("Erro ao carregar detalhes");
      } finally {
        this.loading = false;
      }
    },
    parseRegistros(registros) {
      if (!Array.isArray(registros)) return [];

      const rows = registros
        .filter((row) => typeof row === "string")
        .map((row) => row.split(";").map((column) => column.trim()));

      if (!rows.length) return [];

      const maxColumns = rows.reduce(
        (max, columns) => Math.max(max, columns.length),
        0
      );

      this.headers = Array.from({ length: maxColumns }, (_, index) =>
        index < DETAIL_FIELDS.length
          ? { ...DETAIL_HEADERS[index] }
          : { text: `Coluna ${index + 1}`, value: `coluna${index}` }
      );

      return rows.map((columns) => {
        const entry = {};

        for (let index = 0; index < maxColumns; index++) {
          entry[
            index < DETAIL_FIELDS.length
              ? DETAIL_FIELDS[index]
              : `coluna${index}`
          ] = columns[index] ?? "";
        }

        return entry;
      });
    },
    getExportFileName(extension) {
      const base = this.itemLabel || this.title || "Empresas";
      const date = new Date().toLocaleDateString("pt-BR");

      return `${base} - ${date}.${extension}`;
    },
    exportarCSV() {
      try {
        if (!this.details.length) {
          this.$toast.error("Não há dados para exportar");
          return;
        }

        let csvContent =
          this.headers.map((header) => `"${header.text}"`).join(";") + "\n";

        this.details.forEach((item) => {
          const row = this.headers.map((header) => {
            const value = item[header.value];

            if (value === null || value === undefined) return '""';
            if (typeof value === "object")
              return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
            if (typeof value === "number")
              return value.toString().replace(".", ",");
            return `"${String(value).replace(/"/g, '""')}"`;
          });

          csvContent += row.join(";") + "\n";
        });

        const blob = new Blob(["\ufeff" + csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.setAttribute("href", url);
        link.setAttribute("download", this.getExportFileName("csv"));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.$toast.success("Arquivo CSV exportado com sucesso!");
      } catch (error) {
        console.error("Erro ao exportar para CSV:", error);
        this.$toast.error("Erro ao exportar os dados");
      }
    },
  },
};
</script>

<style scoped>
.companies-dialog {
  border-radius: 10px !important;
  overflow: hidden;
}

.companies-dialog__header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #eef1ff 0%, #f7f8ff 100%);
}

.companies-dialog__heading {
  flex: 1 1 auto;
  min-width: 0;
}

.companies-dialog__title {
  color: #0f2b57;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
}

.companies-dialog__subtitle {
  color: #5b6f90;
  font-size: 13px;
  font-weight: 500;
  margin-top: 2px;
}

.companies-dialog__body {
  padding: 16px 24px;
}

.companies-dialog__table {
  border-radius: 10px;
  border: 1px solid #d9e2ec;
}

.companies-dialog__table >>> .v-data-table__wrapper {
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: auto;
  scrollbar-width: thin;
}

.companies-dialog__actions {
  padding: 12px 20px !important;
}
</style>
